'use client'

import ChevronDownIcon from '@/components/icons/chevron-down'
import useControlled from '@/hooks/use-controlled'
import generateClassNames from '@/utils/generate-class-names'
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from '@floating-ui/react'
import clsx from 'clsx'
import * as React from 'react'
import Checkbox from '../checkbox'
import FormControl from '../form-control'
import Label from '../label'
import './select.scss'

const classes = generateClassNames('select', [
  'placeholder',
  'value',
  'arrow',
  'menu-container',
  'menu',
  'option',
  'checkmark',
])

type FloatingListItemProps = React.HTMLAttributes<HTMLDivElement> & {
  label?: string | null
}

function FloatingListItem(props: FloatingListItemProps) {
  const { children, label, ...other } = props

  const { ref } = useListItem({
    label,
  })

  return (
    <div ref={ref} {...other}>
      {children}
    </div>
  )
}

type SelectValue<Value, Multiple> = Multiple extends true ? Value[] : Value

export interface SelectOption {
  className?: string
  disabled?: boolean
  label: React.ReactNode
  labelText?: string
  value: string
}
export type SelectProps<Multiple extends boolean> = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'defaultValue' | 'onChange'
> & {
  defaultValue?: SelectValue<string, Multiple>
  disabled?: boolean
  label?: React.ReactNode
  multiple?: Multiple
  onChange?: (value: SelectValue<string, Multiple>) => void
  options: SelectOption[]
  placeholder?: string
  renderValue?: (
    selected: SelectValue<SelectOption, Multiple>
  ) => React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  value?: SelectValue<string, Multiple>
  variant?: 'default' | 'transparent'
}

const Select = React.forwardRef(function Select<Multiple extends boolean>(
  props: SelectProps<Multiple>,
  forwardedRef: React.ForwardedRef<HTMLElement>
) {
  const {
    className,
    disabled,
    defaultValue,
    label,
    multiple = false,
    onChange,
    options,
    placeholder,
    renderValue,
    size: selectSize = 'md',
    value: valueProp,
    variant = 'default',
    ...other
  } = props

  const [isOpen, setIsOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue,
  })

  const labelId = React.useId()

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)

    if (open) {
      let index: number | null = null

      if (Array.isArray(value) && value.length > 0) {
        index = options.findLastIndex((option) => value.includes(option.value))
      } else if (typeof value === 'string') {
        index = options.findIndex((option) => option.value === value)
      }

      setSelectedIndex(index)
    }
  }

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    placement: 'bottom',
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      shift({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          })
        },
        padding: 10,
      }),
    ],
  })

  const elementsRef = React.useRef<Array<HTMLElement | null>>([])
  const labelsRef = React.useRef<Array<string | null>>([])
  const isTypingRef = React.useRef(false)

  const handleChange = (optionValue: string, index: number) => {
    setSelectedIndex(index)
    setIsOpen(false)

    if (optionValue === value) {
      return
    }

    setValue(optionValue as SelectValue<string, Multiple>)
    onChange?.(optionValue as SelectValue<string, Multiple>)
  }

  const handleMultipleChange = (optionValue: string, index: number) => {
    setSelectedIndex(index)

    const set = new Set(value)

    if (set.has(optionValue)) {
      set.delete(optionValue)
    } else {
      set.add(optionValue)
    }

    const newValue = Array.from(set)

    setValue(newValue as SelectValue<string, Multiple>)
    onChange?.(newValue as SelectValue<string, Multiple>)
  }

  const handleSelect = multiple ? handleMultipleChange : handleChange

  const handleTypeaheadMatch = (index: number | null) => {
    if (isOpen) {
      setActiveIndex(index)
    }
  }

  const click = useClick(context, { event: 'mousedown' })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
  })
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: handleTypeaheadMatch,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping
    },
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav, typeahead, click]
  )

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 200,
    initial: ({ side }) => ({
      opacity: 0,
      transform: {
        top: 'translateY(10px)',
        bottom: 'translateY(-10px)',
        left: 'translateX(10px)',
        right: 'translateX(-10px)',
      }[side],
    }),
    close: {
      opacity: 0,
    },
  })

  const ref = useMergeRefs([refs.setReference, forwardedRef])

  const renderedValue = React.useMemo(() => {
    const selectedOption = multiple
      ? Array.isArray(value)
        ? value.map((v) => options.find((option) => option.value === v)!)
        : []
      : options.find((option) => option.value === value)

    if (renderValue) {
      return renderValue(selectedOption as SelectValue<SelectOption, Multiple>)
    }

    if (Array.isArray(selectedOption)) {
      return selectedOption.map((option) => option.label).join(', ')
    }

    if (selectedOption) {
      return selectedOption.label
    }

    return null
  }, [multiple, options, renderValue, value])

  return (
    <>
      <FormControl>
        {label ? (
          <Label
            id={labelId}
            disabled={disabled}
            onClick={() => {
              refs.domReference.current?.focus()
            }}
          >
            {label}
          </Label>
        ) : null}

        <div
          aria-autocomplete='none'
          aria-disabled={disabled || undefined}
          aria-labelledby={label ? labelId : undefined}
          className={clsx(
            classes.root,
            `${classes.root}--${selectSize}`,
            `${classes.root}--${variant}`,
            multiple && `${classes.root}--multiple`,
            disabled && `${classes.root}--disabled`,
            className
          )}
          data-state={isOpen ? 'open' : 'closed'}
          ref={ref}
          tabIndex={0}
          {...getReferenceProps(other)}
        >
          {renderedValue ? (
            <span className={classes.value}>{renderedValue}</span>
          ) : (
            <span className={classes.placeholder}>{placeholder}</span>
          )}
          <span className={classes.arrow}>
            <ChevronDownIcon />
          </span>
        </div>
      </FormControl>

      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className={classes['menu-container']}
              style={floatingStyles}
              aria-multiselectable={multiple || undefined}
              {...getFloatingProps()}
            >
              <div
                className={clsx(
                  classes.menu,
                  multiple && `${classes.menu}--multiple`
                )}
                style={styles}
              >
                <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                  {options.map(
                    (
                      {
                        className: optionClassName,
                        disabled: optionDisabled,
                        label: optionLabel,
                        labelText,
                        value: optionValue,
                      },
                      i
                    ) => {
                      const isActive = i === activeIndex
                      const isSelected = Array.isArray(value)
                        ? value.indexOf(optionValue) >= 0
                        : value === optionValue

                      return (
                        <FloatingListItem
                          key={optionValue}
                          role='option'
                          className={clsx(
                            classes.option,
                            isActive && `${classes.option}--active`,
                            !multiple &&
                              isSelected &&
                              `${classes.option}--selected`,
                            optionDisabled && `${classes.option}--disabled`,
                            optionClassName
                          )}
                          tabIndex={isActive ? 0 : -1}
                          aria-selected={isSelected}
                          aria-disabled={optionDisabled || undefined}
                          label={
                            optionDisabled
                              ? null
                              : labelText ??
                                (typeof optionLabel === 'string'
                                  ? optionLabel
                                  : optionValue)
                          }
                          {...getItemProps({
                            // Handle pointer select.
                            onClick() {
                              if (optionDisabled) {
                                return
                              }

                              handleSelect(optionValue, i)
                            },
                            // Handle keyboard select.
                            onKeyDown(event) {
                              if (optionDisabled) {
                                return
                              }

                              if (event.key === 'Enter') {
                                event.preventDefault()
                                handleSelect(optionValue, i)
                              }

                              if (event.key === ' ' && !isTypingRef.current) {
                                event.preventDefault()
                                handleSelect(optionValue, i)
                              }
                            },
                          })}
                        >
                          {multiple ? (
                            <>
                              <Checkbox
                                className={classes.checkmark}
                                checked={isSelected}
                              />
                              {optionLabel}
                            </>
                          ) : (
                            optionLabel
                          )}
                        </FloatingListItem>
                      )
                    }
                  )}
                </FloatingList>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}) as <Multiple extends boolean = false>(
  props: SelectProps<Multiple> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement

export default Select
